import {
  defaultVoiceTalent,
  Design,
  DesignChannelType,
  DesignLanguage,
  DesignModuleNode,
  Public
} from '../types';
import {
  filterNull,
  getCyrb53,
  getInputsFromText,
  getPromptText,
  getVariablesFromText,
  VariableParsing
} from '../utilities';
import { ErrorMessageService } from './error-message-service';
import { IHttpService } from './http-service';
import {
  HttpMethod,
  ResolvedAudios,
  TextToPromptGetAudioProps
} from './types';
import { IWorkflowService } from './workflow-service';

const audioHashPrefix = 'promptAudio-';

/**
 * Some legacy voices were known by different codes on Designs in D2 and in TTP.
 * This mapping goes from Design codes (keys) to TTP codes (values) and should not have new values added to it.
 */
const mappedVoices = new Map<string, string>([
  ['Ashley-en-US', 'Ashley'],
  ['Bridget-en-UK', 'Bridget'],
  ['Francisco-es-US', 'Francisco'],
  ['James-en-US', 'James'],
  ['Kate-en-US', 'Kate'],
  ['Leo-fr-CA', 'Leo'],
  ['Paul-en-US', 'Paul'],
  ['Phil-en-US', 'Phil'],
  ['Violeta-es-US', 'Violeta']
]);

type PrivateGetTtpAudioProps = (
  Pick<TextToPromptGetAudioProps, 'language' | 'node' | 'nodeModule' | 'serialized'>
  & {
    ttsOnly: boolean;
    ttsText: string;
  }
);

type StoredAudioUrl = {
  /** @deprecated No longer used or set. */
  ttsOnly?: boolean;
  url: string;
};

function readStoredAudioUrl(hash: string | null): string | null {
  const storedAudio = (!hash) ? null : localStorage.getItem(hash);
  if (!storedAudio) {
    return null;
  }
  try {
    const storedAudioObject: StoredAudioUrl = JSON.parse(storedAudio);
    return storedAudioObject.url ?? null;
  } catch (e) {
    console.error('Failed to parse stored audio value.', e);
    return null;
  }
}

function storeAudioUrl(hash: string, url: string) {
  const toStore: StoredAudioUrl = { url };
  localStorage.setItem(hash, JSON.stringify(toStore));
}

/** Public interface of @see TextToPromptService. */
export type ITextToPromptService = Public<TextToPromptService>;

/** Service used to connect to all AVS APIs. */
export class TextToPromptService {
  /** Base URL to add to all HTTP requests. */
  static readonly baseUrl = '/ttp/ttp-connector-webapp';

  private _httpService: IHttpService;
  private _errorMessageService: ErrorMessageService;
  private _workflowService: IWorkflowService;

  constructor(
    errorMessageService: ErrorMessageService,
    httpService: IHttpService,
    workflowService: IWorkflowService
  ) {
    this._errorMessageService = errorMessageService;
    this._httpService = httpService;
    this._workflowService = workflowService;
  }

  convertTextToSpeechVoice(voice: string | undefined): string | null {
    if (!voice) {
      return null;
    } else if (mappedVoices.has(voice)) {
      return mappedVoices.get(voice)!;
    }
    return voice;
  }

  /** Resolves VT + TTS audio URLs from cache/WFS; optional explicit TTS generate. */
  async getAudios({
    doFetch = true,
    language,
    node,
    nodeModule,
    serialized
  }: TextToPromptGetAudioProps): Promise<ResolvedAudios> {
    const {
      ttsText,
      ttsHash,
      ttsVoice,
      vtHash,
      vtText,
      vtVoice
    } = this.getPromptTextAndHash(language, node);

    // If there is text & voice, then there will be a hash.
    if (!(ttsHash ?? vtHash)) {
      if (doFetch) {
        const error = new Error('No text to generate audio with.');
        this._errorMessageService.addError(error);
      }
      return { ttsUrl: null, vtUrl: null };
    }

    let vtUrl = readStoredAudioUrl(vtHash);
    let ttsUrl = readStoredAudioUrl(ttsHash);

    // Soft load remaining URLs from WFS recordings.
    if ((!vtUrl && vtHash) || (!ttsUrl && ttsHash)) {
      const promptStatuses = await this._workflowService.getPromptStatus(node.name ?? '', serialized);
      for (const {
        audioUrl,
        text,
        voice
      } of promptStatuses?.voices ?? []) {
        if (!audioUrl) {
          continue;
        }
        const url = audioUrl.replace('{{LINKSTORE_PARAM}}', '/audio/linkstore/');
        if (!vtUrl && vtHash && text === vtText && voice === vtVoice) {
          vtUrl = url;
          storeAudioUrl(vtHash, url);
        }
        if (!ttsUrl && ttsHash && text === ttsText && voice === ttsVoice) {
          ttsUrl = url;
          storeAudioUrl(ttsHash, url);
        }
      }
    }

    if (!doFetch) {
      return { ttsUrl, vtUrl };
    }

    // Explicit generate: always request TTS when a TTS voice/text is available.
    if (!ttsHash) {
      const error = new Error('No matching recorded content or TTS voice.');
      this._errorMessageService.addError(error);
      return { ttsUrl: null, vtUrl };
    }

    const generatedUrl = await this._getNodeAudio({
      language,
      node,
      nodeModule,
      serialized,
      ttsOnly: true,
      ttsText
    });

    if (generatedUrl) {
      ttsUrl = generatedUrl;
      storeAudioUrl(ttsHash, generatedUrl);
    }

    return { ttsUrl, vtUrl };
  }

  getPromptTextAndHash(language: DesignLanguage, node: DesignModuleNode) {
    const {
      language: languageCode,
      ttsFallBackVoice: ttsVoice,
      voice: vtVoice
    } = language;
    let getTextConfig = {
      channel: DesignChannelType.voice,
      languageCode,
      node
    };
    let vtText = getPromptText({ ...getTextConfig, variableParsing: VariableParsing.values });
    let ttsText = getPromptText({ ...getTextConfig, variableParsing: VariableParsing.speakAs });
    // We want to make sure not only the text and variable values (or default values) are the same, but also the voice.
    const ttsHash = (!ttsText || !ttsVoice) ? null : (
      audioHashPrefix + getCyrb53(ttsText + ttsVoice).toString()
    );
    const vtHash = (!vtText || !vtVoice) ? null : (
      audioHashPrefix + getCyrb53(vtText + vtVoice).toString()
    );

    return {
      ttsHash,
      ttsText,
      ttsVoice,
      vtHash,
      vtText,
      vtVoice
    };
  }

  removeAudioFromLocalStorage(language: DesignLanguage, node: DesignModuleNode) {
    const { ttsHash, vtHash } = this.getPromptTextAndHash(language, node);
    if (ttsHash) {
      localStorage.removeItem(ttsHash);
    }
    if (vtHash) {
      localStorage.removeItem(vtHash);
    }
  }

  private async _getNodeAudio({
    language,
    node,
    nodeModule,
    serialized,
    ttsOnly,
    ttsText
  }: PrivateGetTtpAudioProps): Promise<string | null> {
    // Prompt units has to use variables because it keeps duplicates.
    const { language: languageCode } = language;
    const languageInfo = {
      channel: DesignChannelType.voice,
      languageCode,
      node,
      nodeModule,
      serialized
    };
    const inputs = getInputsFromText(languageInfo);
    const variables = getVariablesFromText(languageInfo);
    const promptUnits = variables.map((variable) => {
      const input = inputs.find((input) => {
        return input.varName === variable.fullName;
      });
      return input?.defaultValue ?? '<unknown>';
    }).join('|');
    return this._getTextAudio({
      language,
      promptName: node.name,
      promptUnits,
      serialized,
      text: ttsText,
      ttsOnly,
      skipCachedAudio: true
    });
  }

  async playAudio({
    language,
    serialized = window.apm?.serialized,
    text
  }: {
    language: DesignLanguage;
    serialized: Design;
    text: string;
  }): Promise<boolean> {
    const audioUrl = await this._getTextAudio({
      language,
      serialized,
      text
    });
    if (!audioUrl) {
      return false;
    }

    return new Promise((resolve) => {
      const audio = document.createElement('audio');
      audio.addEventListener('ended', () => resolve(true));
      audio.src = audioUrl;
      audio.setAttribute('autoPlay', 'true');
    });
  }

  private async _getTextAudio({
    language,
    promptName = '',
    promptUnits = '',
    serialized = window.apm?.serialized,
    text,
    ttsOnly,
    skipCachedAudio = false
  }: {
    language: DesignLanguage;
    promptName?: string;
    promptUnits?: string,
    serialized: Design;
    text: string;
    ttsOnly?: boolean;
    /** When true, ask TTP not to reuse an extended cache entry (explicit re-generate). */
    skipCachedAudio?: boolean;
  }): Promise<string | null> {
    const {
      applicationVersion,
      clientName,
      designName
    } = serialized;
    const { language: languageCode, ttsFallBackVoice, voice } = language;
    const ttsVoice = this.convertTextToSpeechVoice(ttsFallBackVoice);

    const response = await this._httpService.fetch({
      query: filterNull({
        branch: applicationVersion,
        client: clientName,
        // extendedCache actually skips the cached audio & stores the new URL in a long-term cache for locking audio.
        extendedCache: `${skipCachedAudio}`,
        language: languageCode,
        module: 'promptdefs',
        project: `${clientName}${designName}`,
        promptName,
        promptUnits,
        ttsFallbackText: text,
        ttsFallbackVoice: ttsVoice,
        ttsOnly: `${ttsOnly ?? !language.voice}`,
        useTtsFallback: (!!ttsVoice).toString(),
        versionName: `${voice}_${languageCode}`.toUpperCase(),
        // Still send VT when present — backend may require it even for TTS-only generation.
        voice: (voice) ? voice : (
          (ttsVoice) ? '' : defaultVoiceTalent
        )
      }),
      method: HttpMethod.post,
      url: 'tts'
    });

    if (!response.ok) {
      const error = new Error('Something went wrong trying to generate the audio. Please try again later.');
      this._errorMessageService.addError(error);
      return null;
    }

    const url = (await response.text()).replace(
      /.*\/ttp-connector-webapp/,
      TextToPromptService.baseUrl
    );
    return url;
  }
}
